import HeaderCostCenter from "../../nc/HeaderCostCenter";
import React, { useEffect, useState } from "react";
import { Container, Card, Table, Badge, Button, Modal, Form, Spinner, Row, Col, Tabs, Tab } from "react-bootstrap";
import { FaPlus, FaTrash, FaPrint, FaCheck, FaTruckLoading, FaEdit, FaSave, FaListAlt } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import api from "../../../axiosConfig";
import { toast } from "react-toastify";
import Select from "react-select";
import { AsyncPaginate } from "react-select-async-paginate";
import LaborEmployees, { LaborEmployeeSummary, splitLaborAmount } from "../../comenzi/LaborEmployees";
import ManoperaProdPickerModal from "../../pickers/ManoperaProdPickerModal";

export default function ComenziProdMasterDetail() {
  const [comenzi, setComenzi] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Modal State
  const [showModal, setShowModal] = useState(false);
  const [activeTab, setActiveTab] = useState("reteta");
  const [formOrder, setFormOrder] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  
  // Extra Material Modal State
  const [showExtraModal, setShowExtraModal] = useState(false);
  const [newExtraMat, setNewExtraMat] = useState(null);
  const [newExtraMatQty, setNewExtraMatQty] = useState(1);
  
  // Manopera Inline State
  const [manoperaPickerOpen, setManoperaPickerOpen] = useState(false);
  const [activeManoperaLineIdx, setActiveManoperaLineIdx] = useState(null);
  
  // Istoric Consum Modal State
  const [selectedLinieForIstoric, setSelectedLinieForIstoric] = useState(null);
  const [newScadere, setNewScadere] = useState({ cantitate: 0, data: new Date().toISOString().split("T")[0] });
  const [stocSursa, setStocSursa] = useState({ stoc: 0, gestiune: "Se încarcă..." });
  
  // Firma settings for Manopera
  const [tarifSkill1, setTarifSkill1] = useState(0.0);
  const [tarifSkill2, setTarifSkill2] = useState(0.0);

  const fetchComenzi = async () => {
    try {
      const res = await api.get("/comenzi-prod/");
      setComenzi(res.data);
    } catch (err) {
      toast.error(`Eroare la încărcarea comenzilor`);
    } finally {
      setLoading(false);
    }
  };

  const fetchFirmaSettings = async () => {
    try {
      const res = await api.get("/firma/");
      if (res.data && res.data.items && res.data.items.length > 0) {
        setTarifSkill1(res.data.items[0].tarif_skill1 || 0.0);
        setTarifSkill2(res.data.items[0].tarif_skill2 || 0.0);
      }
    } catch (e) {
      console.error("Nu am putut incarca setarile firmei", e);
    }
  };

  useEffect(() => {
    fetchFirmaSettings();
    fetchComenzi();
  }, []);

  const handleEdit = async (comanda) => {
    try {
      const res = await api.get(`/comenzi-prod/${comanda.id}`);
      const data = res.data;
      
      // Load angajati details for manopera
      let angajatiList = [];
      try {
        const aRes = await api.get("/angajati/");
        angajatiList = aRes.data || [];
      } catch (e) {
        console.error("Eroare incarcare angajati", e);
      }
      
      data.manopere = (data.manopere || []).map(m => ({
        ...m,
        detalii_angajati: (m.angajati || []).map(a => {
          const match = angajatiList.find(x => x.id === a.angajat_id);
          return {
            bxangajat_id: a.angajat_id,
            nume_angajat: match ? match.nume_prenume : `Angajat #${a.angajat_id}`,
            coeficient: match ? match.coeficient : 1,
            suma: a.timp_alocat
          };
        })
      }));
      
      setFormOrder(data);
      setShowModal(true);
      setActiveTab("reteta");
    } catch (e) {
      toast.error("Eroare la incarcarea detaliilor.");
    }
  };

  const handleCreateNew = () => {
    setFormOrder({
      numar_document: "",
      data_lansare: new Date().toISOString().split("T")[0],
      produs_finit_id: "",
      cantitate_planificata: 1,
      linii: [],
      manopere: [],
      stare: "NOUA"
    });
    setShowModal(true);
    setActiveTab("general");
  };

  const saveOrder = async () => {
    setIsSaving(true);
    try {
      // prepare manopere payload
      const mappedManopere = (formOrder.manopere || []).map(m => {
        const m_cant = Number(m.cantitate) || 0;
        const m_pret = Number(m.pret) || 0;
        const assignments = m.detalii_angajati || [];
        const amount = m_cant * m_pret;
        const shares = splitLaborAmount(amount, assignments);
        const totalWeight = assignments.reduce((sum, a) => sum + Number(a.coeficient || 0), 0);
        
        return {
          id: m.id || null,
          bxmanoperacod_id: m.bxmanoperacod_id,
          cantitate: m_cant,
          pret: m_pret,
          angajati: assignments.map((a, idx) => ({
            angajat_id: a.bxangajat_id,
            timp_alocat: m_pret > 0 && shares[idx]
              ? shares[idx].suma / m_pret
              : totalWeight > 0 ? m_cant * Number(a.coeficient || 0) / totalWeight : 0
          }))
        };
      });

      const payload = {
        ...formOrder,
        cantitate_planificata: Number(formOrder.cantitate_planificata) || 0,
        produs_finit_id: formOrder.produs_finit_id ? Number(formOrder.produs_finit_id) : null,
        manopere: mappedManopere
      };

      if (formOrder.id) {
        const res = await api.put(`/comenzi-prod/${formOrder.id}`, payload);
        toast.success("Comanda actualizată cu succes!");
        fetchComenzi();
        handleEdit(res.data); // Reload to format properly
      } else {
        const res = await api.post("/comenzi-prod/", payload);
        toast.success("Comandă nouă creată!");
        fetchComenzi();
        handleEdit(res.data); // Reload to format properly
      }
    } catch (err) {
      toast.error("Eroare la salvare.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleFinalizare = async () => {
    if (!formOrder || !formOrder.id) return;
    if (!window.confirm("Finalizați comanda (generează NIR Produs Finit)?")) return;
    try {
      await api.post(`/comenzi-prod/${formOrder.id}/finalizare`);
      toast.success("Comanda a fost finalizată!");
      const res = await api.get(`/comenzi-prod/${formOrder.id}`);
      setFormOrder(res.data);
      fetchComenzi();
    } catch (err) {
      toast.error(`Eroare la finalizare.`);
    }
  };

  const handleLinieRetetaChange = (idx, field, value) => {
    const newLinii = [...formOrder.linii];
    newLinii[idx][field] = value;
    setFormOrder({ ...formOrder, linii: newLinii });
  };

  const handleAddScadere = async () => {
    if (!formOrder || !formOrder.id || !selectedLinieForIstoric) return;
    if (!selectedLinieForIstoric.id) {
      toast.warning("Această linie nu a fost salvată încă. Salvați comanda mai întâi!");
      return;
    }
    const val = Number(newScadere.cantitate);
    if (val <= 0) {
      toast.warning("Introduceți o cantitate mai mare ca 0!");
      return;
    }
    if (!newScadere.data) {
      toast.warning("Selectați data scăderii!");
      return;
    }

    try {
      await api.post(`/comenzi-prod/${formOrder.id}/eliberare`, { 
        materiale: [{
          linie_id: selectedLinieForIstoric.id,
          cantitate: val,
          data: newScadere.data
        }] 
      });
      toast.success("Transfer înregistrat cu succes!");
      
      // Refresh order
      const res = await api.get(`/comenzi-prod/${formOrder.id}`);
      setFormOrder(res.data);
      fetchComenzi();
      
      // Update selected linie to show new history
      const updatedLinie = res.data.linii.find(l => l.id === selectedLinieForIstoric.id);
      setSelectedLinieForIstoric(updatedLinie);
      const ramas = Math.max(0, updatedLinie.cantitate_necesara - (updatedLinie.cantitate_consumata || 0));
      setNewScadere({ cantitate: ramas, data: new Date().toISOString().split("T")[0] });
      
      // Refresh stoc
      const stocRes = await api.get(`/comenzi-prod/stoc-material/${updatedLinie.materie_prima_id}`);
      setStocSursa(stocRes.data);
      if (stocRes.data.eroare) toast.warning(stocRes.data.eroare);

    } catch (err) {
      toast.error(err.response?.data?.detail || "Eroare la salvarea transferului.");
    }
  };

  const handleOpenIstoric = async (l) => {
    setSelectedLinieForIstoric(l);
    setStocSursa({ stoc: "...", gestiune: "Se încarcă..." });
    const ramas = Math.max(0, l.cantitate_necesara - (l.cantitate_consumata || 0));
    setNewScadere({ cantitate: ramas, data: new Date().toISOString().split("T")[0] });

    try {
      const res = await api.get(`/comenzi-prod/stoc-material/${l.materie_prima_id}`);
      setStocSursa(res.data);
      if (res.data.eroare) toast.warning(res.data.eroare);
    } catch (e) {
      setStocSursa({ stoc: "?", gestiune: "Eroare la încărcare" });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Sigur doriți să ștergeți comanda?")) return;
    try {
      await api.delete(`/comenzi-prod/${id}`);
      toast.success("Comandă ștearsă!");
      fetchComenzi();
    } catch (err) {
      toast.error("Eroare la ștergere.");
    }
  };

  const loadRetetaMaterials = async (overrideProductId = null) => {
    const prodId = overrideProductId || (formOrder && formOrder.produs_finit_id);
    if (!prodId) return;

    try {
      const res = await api.get(`/bxreteta/activa/${prodId}`);
      const recipe = res.data;
      
      if (!recipe || !recipe.linii || recipe.linii.length === 0) {
        toast.warning("Produsul selectat nu are nicio rețetă activă cu materiale!");
        return;
      }

      setFormOrder(prev => {
        const existingLinii = prev.linii || [];
        const qtyMultiplier = prev.cantitate_planificata || 1;
        let addedCount = 0;
        const updatedLinii = [...existingLinii];

        recipe.linii.forEach(rl => {
          const alreadyExists = updatedLinii.some(l => l.materie_prima_id === rl.material_id);
          if (!alreadyExists) {
            updatedLinii.push({
              materie_prima_id: rl.material_id,
              cantitate_necesara: rl.cantitate * qtyMultiplier,
              cantitate_consumata: 0,
              istoric_consum: []
            });
            addedCount++;
          }
        });

        if (addedCount > 0) {
          toast.success(`Au fost încărcate ${addedCount} materiale noi din rețetă! (Nu uitați să salvați)`);
          return { ...prev, linii: updatedLinii, reteta_id: recipe.id };
        }
        return prev;
      });

    } catch (err) {
      if (err.response && err.response.status === 404) {
        toast.warning("Produsul selectat nu are nicio rețetă definită!");
      } else {
        toast.error("Eroare la verificarea rețetei.");
      }
    }
  };

  const loadProduse = async (search, loadedOptions, { page }) => {
    try {
      const res = await api.get("/bxpart/", { params: { search, skip: (page - 1) * 20, limit: 20 } });
      const items = res.data.items || res.data;
      return {
        options: items.map(p => ({ value: p.id, label: `[${p.cod}] ${p.denumire}`, raw: p })),
        hasMore: items.length === 20,
        additional: { page: page + 1 }
      };
    } catch (e) {
      return { options: [], hasMore: false };
    }
  };

  const handleAddExtraMat = async () => {
    if (!formOrder.id) {
      toast.warning("Vă rugăm să salvați comanda mai întâi!");
      return;
    }
    if (!newExtraMat) {
      toast.warning("Selectați un material.");
      return;
    }
    try {
      const payload = {
        materie_prima_id: newExtraMat.value,
        cantitate_necesara: newExtraMatQty,
        cantitate_consumata: 0,
        is_extra: true
      };
      await api.post(`/comenzi-prod/${formOrder.id}/linii`, payload);
      toast.success("Material adăugat! Îl puteți transfera acum din lista Materiale Extra.");
      setShowExtraModal(false);
      setNewExtraMat(null);
      setNewExtraMatQty(1);
      // Refresh order
      const res = await api.get(`/comenzi-prod/${formOrder.id}`);
      setFormOrder(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Eroare la adăugarea materialului extra.");
    }
  };

  const handleAddManopera = () => {
    setFormOrder({
      ...formOrder,
      manopere: [
        ...(formOrder.manopere || []),
        { id: null, bxmanoperacod_id: null, bxmanoperacod_cod: "", bxmanoperacod_denumire: "", cantitate: 1, pret: 0, angajati: [] }
      ]
    });
  };

  const handleUpdateManopera = (idx, field, value) => {
    const updated = [...(formOrder.manopere || [])];
    updated[idx][field] = value;
    setFormOrder({ ...formOrder, manopere: updated });
  };

  const handleRemoveManopera = (idx) => {
    const updated = [...(formOrder.manopere || [])];
    updated.splice(idx, 1);
    setFormOrder({ ...formOrder, manopere: updated });
  };

  const handlePickManopera = (part) => {
    if (activeManoperaLineIdx !== null) {
      handleUpdateManopera(activeManoperaLineIdx, "bxmanoperacod_id", part.id);
      handleUpdateManopera(activeManoperaLineIdx, "bxmanoperacod_cod", part.code || part.cod);
      handleUpdateManopera(activeManoperaLineIdx, "bxmanoperacod_denumire", part.name || part.denumire);
      
      const hours = Number(part.minute) > 0 ? Number(part.minute) / 60 : 1;
      const rate = Number(part.skill) === 1 ? Number(tarifSkill1) : Number(part.skill) === 2 ? Number(tarifSkill2) : 0;
      
      handleUpdateManopera(activeManoperaLineIdx, "cantitate", hours);
      handleUpdateManopera(activeManoperaLineIdx, "pret", rate);
    }
    setManoperaPickerOpen(false);
  };

  const loadContacte = async (search, loadedOptions, { page }) => {
    try {
      const res = await api.get("/contacte/", { params: { search, skip: (page - 1) * 20, limit: 20 } });
      const items = res.data.items || res.data;
      return {
        options: items.map(c => ({ value: c.id, label: c.denumire || c.bxdenumire || `Client ${c.id}` })),
        hasMore: items.length === 20,
        additional: { page: page + 1 }
      };
    } catch (e) {
      return { options: [], hasMore: false };
    }
  };

  const loadManopere = async (search, loadedOptions, { page }) => {
    try {
      const res = await api.get("/bxpart/", { params: { search, skip: (page - 1) * 20, limit: 20, tip_grupa_produse: 3 } });
      const items = res.data.items || res.data;
      return {
        options: items.map(m => ({ value: m.id, label: `[${m.cod}] ${m.denumire}`, raw: m })),
        hasMore: items.length === 20,
        additional: { page: page + 1 }
      };
    } catch (e) {
      return { options: [], hasMore: false };
    }
  };

  const loadAngajati = async (search, loadedOptions, { page }) => {
    try {
      const res = await api.get("/angajati/");
      // The angajati endpoint returns a flat list and doesn't support pagination/search natively
      let items = res.data || [];
      if (search) {
        items = items.filter(a => a.nume_prenume.toLowerCase().includes(search.toLowerCase()));
      }
      return {
        options: items.map(a => ({ value: a.id, label: a.nume_prenume })),
        hasMore: false,
        additional: { page: 1 }
      };
    } catch (e) {
      return { options: [], hasMore: false };
    }
  };

  if (loading) return <Container className="mt-5 text-center"><Spinner animation="border" /></Container>;

  return (
    <Container fluid className="mt-4 px-4">
      {/* HEADER PAGE */}
      <div className="d-flex justify-content-between align-items-center mb-4 pb-2 border-bottom">
        <div>
          <h2 className="fw-bold text-dark mb-1">Comenzi Producție</h2>
          <p className="text-muted mb-0">Listă de comenzi tip Master-Detail, asemănător Service Auto.</p>
        </div>
        <Button variant="primary" onClick={handleCreateNew}>
          <FaPlus className="me-2" /> Adaugă Comandă
        </Button>
      </div>

      {/* MASTER GRID */}
      <Card className="shadow-sm">
        <Card.Body className="p-0">
          <Table responsive hover striped className="mb-0 align-middle">
            <thead className="table-light">
              <tr>
                <th>ID / Număr</th>
                <th>Dată Lansare</th>
                <th>Stare</th>
                <th>Produs Finit</th>
                <th>Cantitate</th>
                <th className="text-center">Acțiuni</th>
              </tr>
            </thead>
            <tbody>
              {comenzi.map((c) => (
                <tr key={c.id} style={{ cursor: "pointer" }} onClick={(e) => {
                  // Prevent opening if clicked on actions
                  if (e.target.closest("button")) return;
                  handleEdit(c);
                }}>
                  <td className="fw-bold text-primary">#{c.numar_document}</td>
                  <td>{c.data_lansare}</td>
                  <td>
                    <Badge bg={c.stare === "NOUA" ? "info" : c.stare === "FINALIZATA" ? "success" : "warning"}>
                      {c.stare}
                    </Badge>
                  </td>
                  <td>{c.produs_finit_cod ? `[${c.produs_finit_cod}] ${c.produs_finit_denumire}` : `ID: ${c.produs_finit_id}`}</td>
                  <td>{c.cantitate_planificata}</td>
                  <td className="text-center">
                    <Button size="sm" variant="outline-primary" className="me-1" onClick={(e) => { e.stopPropagation(); handleEdit(c); }} title="Editează">
                      <FaEdit />
                    </Button>
                    <Button size="sm" variant="outline-danger" onClick={(e) => { e.stopPropagation(); handleDelete(c.id); }} title="Șterge">
                      <FaTrash />
                    </Button>
                  </td>
                </tr>
              ))}
              {comenzi.length === 0 && <tr><td colSpan="6" className="text-center text-muted py-3">Nu există comenzi.</td></tr>}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      {/* DETAIL MODAL */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="xl" backdrop="static">
        <Modal.Header closeButton className="bg-primary text-white">
          <Modal.Title className="fw-bold d-flex align-items-center">
            <span>
              {formOrder?.id ? `Editare Comandă Producție #${formOrder.numar_document}` : "Adăugare Comandă Producție"}
            </span>
            {formOrder?.stare && (
              <span className={`badge ${formOrder.stare === "NOUA" ? "bg-info" : formOrder.stare === "FINALIZATA" ? "bg-success" : "bg-warning"} ms-3 fs-6 text-white`}>
                STARE: {formOrder.stare}
              </span>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="bg-light p-4">
          {formOrder && (
            <>
              {/* TABS */}
              <Tabs activeKey={activeTab} onSelect={(k) => { setActiveTab(k); if(k === 'reteta') loadRetetaMaterials(); }} className="mb-3 custom-tabs">
                <Tab eventKey="general" title={<span><FaListAlt className="me-2"/>Detalii Generale</span>}>
                  <Card className="mb-3 border-0 shadow-sm mt-3">
                    <Card.Body>
                      <Row className="g-3">
                        <Col md={3}>
                          <Form.Group>
                            <Form.Label className="fw-semibold text-muted small text-uppercase">Număr Document</Form.Label>
                            <Form.Control 
                              type="text" 
                              value={formOrder.numar_document} 
                              onChange={(e) => setFormOrder({...formOrder, numar_document: e.target.value})} 
                              size="sm"
                            />
                          </Form.Group>
                        </Col>
                        <Col md={3}>
                          <Form.Group>
                            <Form.Label className="fw-semibold text-muted small text-uppercase">Dată Lansare</Form.Label>
                            <Form.Control 
                              type="date" 
                              value={formOrder.data_lansare} 
                              onChange={(e) => setFormOrder({...formOrder, data_lansare: e.target.value})} 
                              size="sm"
                            />
                          </Form.Group>
                        </Col>
                        <Col md={4}>
                          <Form.Group>
                            <Form.Label className="fw-semibold text-muted small text-uppercase">Produs Finit (Rețetă)</Form.Label>
                            {formOrder.id ? (
                              <Form.Control type="text" value={formOrder.produs_finit_cod ? `[${formOrder.produs_finit_cod}] ${formOrder.produs_finit_denumire}` : `ID: ${formOrder.produs_finit_id}`} disabled size="sm"/>
                            ) : (
                              <AsyncPaginate
                                loadOptions={loadProduse}
                                additional={{ page: 1 }}
                                menuPortalTarget={document.body}
                                styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                                value={formOrder.produs_finit_id ? { value: formOrder.produs_finit_id, label: formOrder.produs_finit_cod ? `[${formOrder.produs_finit_cod}] ${formOrder.produs_finit_denumire}` : `ID: ${formOrder.produs_finit_id}` } : null}
                                onChange={(v) => {
                                  const newId = v ? v.value : "";
                                  setFormOrder({
                                    ...formOrder, 
                                    produs_finit_id: newId,
                                    produs_finit_cod: v?.raw?.cod || "",
                                    produs_finit_denumire: v?.raw?.denumire || ""
                                  });
                                  if (activeTab === 'reteta' && newId) {
                                    loadRetetaMaterials(newId);
                                  }
                                }}
                              />
                            )}
                          </Form.Group>
                        </Col>
                        <Col md={2}>
                          <Form.Group>
                            <Form.Label className="fw-semibold text-muted small text-uppercase">Cantitate</Form.Label>
                            <Form.Control 
                              type="number" 
                              value={formOrder.cantitate_planificata} 
                              onChange={(e) => setFormOrder({...formOrder, cantitate_planificata: e.target.value})} 
                              size="sm"
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                      
                      <Row className="g-3 mt-1">
                        <Col md={6}>
                          <Form.Group>
                            <Form.Label className="fw-semibold text-muted small text-uppercase">Client / Contact (Opțional)</Form.Label>
                            <AsyncPaginate
                              loadOptions={loadContacte}
                              additional={{ page: 1 }}
                              menuPortalTarget={document.body}
                              styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                              value={formOrder.contact_id ? { value: formOrder.contact_id, label: `Contact ID: ${formOrder.contact_id}` } : null}
                              onChange={(v) => setFormOrder({...formOrder, contact_id: v ? v.value : null})}
                              isClearable
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                      <HeaderCostCenter value={formOrder.centru_cost_id} disabled={formOrder.stare === "FINALIZATA"} onChange={id => setFormOrder(prev => ({ ...prev, centru_cost_id: id }))} />

                    </Card.Body>
                  </Card>
                </Tab>

                <Tab eventKey="reteta" title={<span><FaListAlt className="me-2"/>Rețetă & Materiale</span>}>
                  <Card className="border-0 shadow-sm mb-3">
                    <Card.Body className="p-0">
                      <Table size="sm" bordered responsive className="mb-0 align-middle">
                        <thead className="table-secondary">
                          <tr>
                            <th>Materie Primă (ID)</th>
                            <th>Necesar</th>
                            <th>Transferat</th>
                            <th>Rămas</th>
                            <th>Istoric Transferuri</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(formOrder.linii || []).filter(l => !l.is_extra).map((l, index) => {
                            const ramas = Math.max(0, l.cantitate_necesara - (l.cantitate_consumata || 0));
                            return (
                            <tr key={l.id || Math.random()}>
                              <td className="fw-bold">{l.materie_prima_cod ? `[${l.materie_prima_cod}] ${l.materie_prima_denumire}` : `ID: ${l.materie_prima_id}`}</td>
                              <td>{l.cantitate_necesara}</td>
                              <td className="text-success fw-bold">{l.cantitate_consumata || 0}</td>
                              <td className="text-danger fw-bold">{ramas}</td>
                              <td>
                                  <div className="d-flex align-items-center justify-content-between">
                                    <span className="small text-muted me-2">
                                      {(l.istoric_consum || []).length} înregistrări
                                    </span>
                                    <Button size="sm" variant="outline-primary" onClick={() => handleOpenIstoric(l)}>
                                      Detalii / Transfer
                                    </Button>
                                  </div>
                              </td>
                            </tr>
                          )})}
                          {(!formOrder.linii || formOrder.linii.length === 0) && (
                            <tr><td colSpan="5" className="text-center py-3 text-muted">Nu sunt materiale. (Rețeta se încarcă la salvare)</td></tr>
                          )}
                        </tbody>
                      </Table>
                    </Card.Body>
                  </Card>
                </Tab>

                <Tab eventKey="extra" title={<span><FaPlus className="me-2"/>Materiale Extra</span>}>
                  <Card className="border-0 shadow-sm mb-3">
                    <Card.Header className="bg-white d-flex justify-content-between align-items-center">
                      <span className="fw-bold text-muted">Materiale Consumate Suplimentar</span>
                      <Button size="sm" variant="success" onClick={() => {
                        if (!formOrder.id) {
                          toast.warning("Salvați comanda mai întâi!");
                          return;
                        }
                        setShowExtraModal(true);
                      }}>
                        <FaPlus className="me-1" /> Adaugă Material Extra
                      </Button>
                    </Card.Header>
                    <Card.Body className="p-0">
                      <Table size="sm" bordered responsive className="mb-0 align-middle">
                        <thead className="table-secondary">
                          <tr>
                            <th>Materie Primă (ID)</th>
                            <th>Necesar Extra</th>
                            <th>Transferat</th>
                            <th>Rămas de Transferat</th>
                            <th>Istoric Transferuri</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(formOrder.linii || []).filter(l => l.is_extra).map((l) => {
                            const ramas = Math.max(0, l.cantitate_necesara - (l.cantitate_consumata || 0));
                            return (
                            <tr key={l.id || Math.random()}>
                              <td className="fw-bold">{l.materie_prima_cod ? `[${l.materie_prima_cod}] ${l.materie_prima_denumire}` : `ID: ${l.materie_prima_id}`}</td>
                              <td>{l.cantitate_necesara}</td>
                              <td className="text-success fw-bold">{l.cantitate_consumata || 0}</td>
                              <td className="text-danger fw-bold">{ramas}</td>
                              <td>
                                <div className="d-flex align-items-center justify-content-between">
                                  <span className="small text-muted me-2">
                                    {(l.istoric_consum || []).length} înregistrări
                                  </span>
                                  <Button size="sm" variant="outline-primary" onClick={() => handleOpenIstoric(l)}>
                                    Detalii / Transfer
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          )})}
                          {(formOrder.linii || []).filter(l => l.is_extra).length === 0 && (
                            <tr><td colSpan="5" className="text-center py-3 text-muted">Nu sunt materiale extra.</td></tr>
                          )}
                        </tbody>
                      </Table>
                    </Card.Body>
                  </Card>
                </Tab>

                <Tab eventKey="manopera" title={<span><FaListAlt className="me-2"/>Manoperă & Angajați</span>}>
                  <div className="table-responsive mt-3">
                    <Table size="sm" bordered className="mb-3 align-middle bg-white">
                      <thead className="table-secondary">
                        <tr>
                          <th style={{ width: "220px" }}>Cod Manoperă</th>
                          <th style={{ width: "100px" }}>Cantitate / Ore</th>
                          <th style={{ width: "120px" }}>Preț (lei)</th>
                          <th style={{ width: "120px" }}>Valoare</th>
                          <th>Detalii Angajați (Repartizare)</th>
                          <th style={{ width: "60px" }} className="text-center">Sterge</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(formOrder.manopere || []).map((m, idx) => (
                          <tr key={idx}>
                            <td>
                              <div className="d-flex gap-1 align-items-center">
                                <Form.Control
                                  type="text"
                                  size="sm"
                                  value={m.bxmanoperacod_cod ? `[${m.bxmanoperacod_cod}] ${m.bxmanoperacod_denumire}` : m.bxmanoperacod_id ? `ID: ${m.bxmanoperacod_id}` : ""}
                                  readOnly
                                  style={{ cursor: "pointer" }}
                                  onClick={() => {
                                    setActiveManoperaLineIdx(idx);
                                    setManoperaPickerOpen(true);
                                  }}
                                  placeholder="Alege manoperă..."
                                />
                                <Button
                                  variant="outline-secondary"
                                  size="sm"
                                  className="py-0 px-2"
                                  onClick={() => {
                                    setActiveManoperaLineIdx(idx);
                                    setManoperaPickerOpen(true);
                                  }}
                                >
                                  ?
                                </Button>
                              </div>
                            </td>
                            <td>
                              <Form.Control
                                type="number"
                                size="sm"
                                step="any"
                                value={m.cantitate ?? 1}
                                onChange={(e) => handleUpdateManopera(idx, "cantitate", e.target.value)}
                              />
                            </td>
                            <td>
                              <Form.Control
                                type="number"
                                size="sm"
                                step="any"
                                value={m.pret ?? 0}
                                onChange={(e) => handleUpdateManopera(idx, "pret", e.target.value)}
                              />
                            </td>
                            <td>
                              <Form.Control
                                type="number"
                                size="sm"
                                value={Number((m.cantitate || 0) * (m.pret || 0)).toFixed(2)}
                                disabled
                              />
                            </td>
                            <td>
                              <LaborEmployees 
                                value={m.detalii_angajati || []} 
                                amount={Number(m.cantitate || 0) * Number(m.pret || 0)} 
                                onChange={value => handleUpdateManopera(idx, "detalii_angajati", value)} 
                              />
                            </td>
                            <td className="text-center">
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => handleRemoveManopera(idx)}
                              >
                                X
                              </Button>
                            </td>
                          </tr>
                        ))}
                        {(!formOrder.manopere || formOrder.manopere.length === 0) && (
                          <tr><td colSpan="6" className="text-center py-3 text-muted">Fără manoperă alocată.</td></tr>
                        )}
                      </tbody>
                    </Table>
                    <LaborEmployeeSummary rows={(formOrder.manopere || []).map(row => ({
                      ...row,
                      bxqty_manopera: row.cantitate,
                      bxpret_manopera: row.pret,
                    }))} />
                    
                    <Button variant="success" size="sm" onClick={handleAddManopera} className="fw-semibold">
                      <FaPlus className="me-1" /> Adaugă Manoperă
                    </Button>
                  </div>
                </Tab>

              </Tabs>
            </>
          )}
        </Modal.Body>
        <Modal.Footer className="bg-white justify-content-between">
          <div>
             {formOrder?.id && formOrder.stare !== "FINALIZATA" && (
                <Button variant="success" className="me-2" onClick={handleFinalizare}>
                  <FaCheck className="me-2"/> Închide Deviz (Bon Producție)
                </Button>
             )}
             {formOrder?.id && (
                <Button variant="secondary" onClick={() => navigate(`/comenzi-prod/${formOrder.id}/print`)}>
                  <FaPrint className="me-2"/> Printează
                </Button>
             )}
          </div>
          <div>
            <Button variant="light" className="me-2 border" onClick={() => setShowModal(false)}>Renunță</Button>
            <Button variant="primary" onClick={saveOrder} disabled={isSaving}>
              {isSaving ? <Spinner size="sm" /> : <><FaSave className="me-2"/> Salvează</>}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      {/* NESTED MODAL FOR ISTORIC TRANSFER */}
      <Modal show={!!selectedLinieForIstoric} onHide={() => setSelectedLinieForIstoric(null)} backdrop="static" size="lg">
        <Modal.Header closeButton className="bg-light">
          <Modal.Title className="fw-bold fs-5">Istoric & Adăugare Transfer (Depozit -{">"} Producție)</Modal.Title>
        </Modal.Header>
        <Modal.Body className="bg-light">
          {selectedLinieForIstoric && (
            <Row>
              <Col md={7}>
                <h6 className="fw-bold mb-3">Istoric Transferuri Existent</h6>
                <Table size="sm" bordered responsive className="bg-white">
                  <thead className="table-secondary">
                    <tr>
                      <th>Dată</th>
                      <th>Cantitate Transferată</th>
                      <th>Bon Transfer</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(selectedLinieForIstoric.istoric_consum || []).map(ist => (
                      <tr key={ist.id}>
                        <td>{ist.data}</td>
                        <td className="fw-bold text-success">{ist.cantitate}</td>
                        <td className="text-muted">BT #{ist.stocdoc_id}</td>
                      </tr>
                    ))}
                    {(!selectedLinieForIstoric.istoric_consum || selectedLinieForIstoric.istoric_consum.length === 0) && (
                      <tr><td colSpan="3" className="text-center text-muted py-2">Fără istoric.</td></tr>
                    )}
                  </tbody>
                </Table>
              </Col>
              
              <Col md={5}>
                <Card className="border-0 shadow-sm">
                  <Card.Body>
                    <h6 className="fw-bold mb-3 text-primary">Transferă din Depozit</h6>
                    <div className="mb-3 p-2 bg-light rounded small border">
                      Existent pe stoc în depozit: <b className="text-info fs-6">{stocSursa.stoc}</b> <br/>
                      <span className="text-muted">(Gestiune: {stocSursa.gestiune})</span>
                    </div>
                    <Form.Group className="mb-3">
                      <Form.Label className="small fw-bold text-muted">Data Transferului</Form.Label>
                      <Form.Control 
                        type="date" 
                        size="sm"
                        value={newScadere.data} 
                        onChange={e => setNewScadere({...newScadere, data: e.target.value})} 
                      />
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <Form.Label className="small fw-bold text-muted">Cantitate de Transferat</Form.Label>
                      <Form.Control 
                        type="number" 
                        size="sm"
                        step="any"
                        value={newScadere.cantitate} 
                        onChange={e => setNewScadere({...newScadere, cantitate: e.target.value})} 
                      />
                    </Form.Group>
                    <div className="text-end">
                      <Button variant="success" size="sm" onClick={handleAddScadere}>
                        <FaPlus className="me-1"/> Confirmă Transferul
                      </Button>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}
        </Modal.Body>
      </Modal>

      {/* MODAL MATERIAL EXTRA */}
      <Modal show={showExtraModal} onHide={() => setShowExtraModal(false)} backdrop="static">
        <Modal.Header closeButton className="bg-light">
          <Modal.Title className="fw-bold fs-5">Adaugă Material Extra</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label className="small fw-bold text-muted">Caută Materie Primă</Form.Label>
            <AsyncPaginate
              loadOptions={loadProduse}
              additional={{ page: 1 }}
              menuPortalTarget={document.body}
              styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
              value={newExtraMat}
              onChange={setNewExtraMat}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label className="small fw-bold text-muted">Cantitate Extra</Form.Label>
            <Form.Control 
              type="number" step="any"
              value={newExtraMatQty}
              onChange={e => setNewExtraMatQty(e.target.value)}
            />
          </Form.Group>
          <div className="text-muted small mb-3">
            Notă: Aceasta este cantitatea necesară (estimată). Consumul efectiv se va înregistra din fereastra Detalii / Transfer!
          </div>
          <div className="text-end">
            <Button variant="success" onClick={handleAddExtraMat}>Adaugă în listă</Button>
          </div>
        </Modal.Body>
      </Modal>

      {/* Manopera Picker Modal */}
      <ManoperaProdPickerModal
        show={manoperaPickerOpen}
        onHide={() => {
          setManoperaPickerOpen(false);
          setActiveManoperaLineIdx(null);
        }}
        onPick={handlePickManopera}
      />

    </Container>
  );
}
